import { FC, useMemo } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbCheck, TbClock } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { WorkspaceIdentityInput } from '@/services/database';
import { DropdownMenuPanel } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { WorkspaceIdentity } from '@/types';

import AdvancedSettingsSectionHeader from '../../../components/AdvancedSettingsSectionHeader';

interface SessionDurationSectionProps {
    identity: WorkspaceIdentity | null;
    isEnterprise: boolean;
    saving: boolean;
    updateIdentity: (input: WorkspaceIdentityInput, toastLabel: string) => void;
}

const sessionDurationOptions = [
    { days: 1, label: '1 Day' },
    { days: 7, label: '1 Week' },
    { days: 14, label: '2 Weeks' },
    { days: 30, label: '30 Days' },
    { days: 60, label: '2 Months' },
    { days: 90, label: '3 Months' },
    { days: 180, label: '6 Months' },
];

const SessionDurationSection: FC<SessionDurationSectionProps> = ({
    identity,
    isEnterprise,
    saving,
    updateIdentity,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const selectedDuration = useMemo(
        () =>
            sessionDurationOptions.find((option) => option.days === identity?.sessionDurationDays) ??
            sessionDurationOptions.find((option) => option.days === 30)!,
        [identity?.sessionDurationDays],
    );

    return (
        <AdvancedSettingsSectionHeader
            description='Define the maximum amount of time a workspace session can remain valid before reauthentication.'
            icon={<TbClock size='1rem' />}
            plan='enterprise'
            title='User Session Duration'
            extra={
                <Dropdown
                    disabled={!isEnterprise || saving}
                    popupRender={() => (
                        <DropdownMenuPanel
                            style={{ minWidth: 220 }}
                            onClick={({ key }) => {
                                const option = sessionDurationOptions.find((duration) => duration.days === Number(key));

                                if (!option) {
                                    return;
                                }

                                updateIdentity(
                                    { sessionDurationDays: option.days },
                                    `Updated session duration to ${option.label}`,
                                );
                            }}
                        >
                            <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>
                                Session Duration
                            </small>
                            <Menu.Divider />
                            {sessionDurationOptions.map((option) => (
                                <Menu.Item
                                    key={option.days}
                                    icon={
                                        option.days === selectedDuration.days ? (
                                            <TbCheck style={{ color: token.colorPrimary }} />
                                        ) : (
                                            <TbClock style={{ color: token.colorTextTertiary }} />
                                        )
                                    }
                                >
                                    {option.label}
                                </Menu.Item>
                            ))}
                        </DropdownMenuPanel>
                    )}
                    placement='bottomRight'
                    trigger={['click']}
                >
                    <DropdownButton disabled={!isEnterprise || saving}>{selectedDuration.label}</DropdownButton>
                </Dropdown>
            }
        />
    );
};

export default SessionDurationSection;
